// Ubicación del dispositivo + dirección legible (geocodificación INVERSA).
//
// Separado de factoryGeocoding.ts a propósito: aquel resuelve nombres de
// fábrica → coordenadas para el mapa de Estadísticas y tiene su propia caché en
// IndexedDB. Aquí es al revés (coordenadas → dirección) y es una acción puntual
// disparada por el usuario, sin caché ni cola.
//
// Reutiliza la misma clave de Azure Maps (__AZURE_MAPS_KEY__) que ya usa el
// geocoder de fábricas.

export interface Coords {
  latitude: number;
  longitude: number;
  /** Precisión del GPS en metros, si el dispositivo la reporta. */
  accuracy?: number;
}

export type LocationErrorReason =
  | 'unsupported'    // el navegador no expone geolocalización
  | 'denied'         // el usuario denegó el permiso
  | 'unavailable'    // sin señal / el sistema no pudo posicionar
  | 'timeout';       // tardó demasiado

export class LocationError extends Error {
  // Campo declarado aparte (no como propiedad de constructor): el proyecto
  // compila con `erasableSyntaxOnly`, que no admite ese azúcar de TypeScript.
  readonly reason: LocationErrorReason;
  constructor(reason: LocationErrorReason) {
    super(reason);
    this.name = 'LocationError';
    this.reason = reason;
  }
}

import { reverseGeocodeApi } from './api';

// Posición actual del dispositivo. `enableHighAccuracy` porque el controlador
// está EN la fábrica: interesa la posición fina, no la de la antena.
export function getCurrentCoords(timeoutMs = 10000): Promise<Coords> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new LocationError('unsupported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: typeof pos.coords.accuracy === 'number' ? pos.coords.accuracy : undefined,
      }),
      (err) => {
        const reason: LocationErrorReason =
          err.code === err.PERMISSION_DENIED ? 'denied'
          : err.code === err.TIMEOUT ? 'timeout'
          : 'unavailable';
        reject(new LocationError(reason));
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 }
    );
  });
}

// Formato de reserva cuando no hay dirección: coordenadas legibles. Mejor esto
// que dejar el campo vacío — el dato queda registrado igual.
export function formatCoords(c: Coords): string {
  return `${c.latitude.toFixed(6)}, ${c.longitude.toFixed(6)}`;
}

// Coordenadas → dirección postal, a través de NUESTRA API.
//
// No se llama a Azure Maps desde el navegador: la clave viajaría en el bundle y
// la cuenta exige lista blanca de orígenes CORS, así que cualquier dominio o
// puerto no listado devolvía 403 y esto acababa devolviendo null en silencio.
// El servidor no manda cabecera Origin, así que el problema desaparece.
//
// Devuelve null si no se pudo resolver: el caller decide el respaldo.
export async function reverseGeocode(c: Coords, language = 'es-ES'): Promise<string | null> {
  try {
    const res = await reverseGeocodeApi(c.latitude, c.longitude, language);
    const address = res.data?.address;
    return address && address.trim() ? address.trim() : null;
  } catch {
    return null;
  }
}

// Atajo para la UI: posición + dirección en una sola llamada. Si la
// geocodificación inversa no resuelve, devuelve las coordenadas formateadas
// para que el campo nunca se quede vacío tras pulsar el botón.
export async function getCurrentAddress(language?: string): Promise<{ text: string; coords: Coords }> {
  const coords = await getCurrentCoords();
  const address = await reverseGeocode(coords, language);
  return { text: address ?? formatCoords(coords), coords };
}
